import {
  SongPackLoader,
  isSongPackError,
  type Difficulty,
  type LoadedSongPack,
} from '../songpack/index.js';
import {
  prepareAudio,
  prepareCover,
  type AudioDecoder,
  type PreparedAudio,
  type SelectedFile,
} from './AudioSource.js';
import {
  ChartGenerationError,
  type ChartGenerationClient,
  type ChartGenerationProgress,
  type ChartGenerationSummary,
} from './ChartGenerationClient.js';
import { derivePackId } from './PackId.js';
import type { ChartTuning, GeneratorMode } from './ChartTuning.js';
import type { ChartDirection } from './ChartDirection.js';

/**
 * CREATE SONG, as a sequence with no screen attached to it.
 *
 * ```
 * chosen file -> prepareAudio -> ChartGenerationClient -> SongPack v1 bytes
 *                                                       -> SongPackLoader (proof)
 * ```
 *
 * Three things live here rather than in the UI, because they are rules and not
 * presentation.
 *
 * **One generation at a time.** A second `create()` while one is running is
 * refused outright. Two concurrent runs would race for the same engine, and the
 * loser's archive would arrive after the winner had already been loaded.
 *
 * **The archive is read before it is accepted.** The bytes go through the same
 * `SongPackLoader` the game uses, here, at creation time. If the engine ever
 * produced something this runtime cannot read, the failure belongs in the CREATE
 * SONG screen where it is actionable — not later, as a broken session. This is
 * also the concrete guarantee that Unity and Three.js are not drifting apart:
 * the pack is validated by the reader both of them share.
 *
 * **Failure is contained.** Every exit throws a described error and returns the
 * service to `idle`. A Python traceback, an unreachable engine and a cancelled
 * run all leave the game exactly as it was.
 */

export type CreationPhase =
  | 'idle'
  | 'preparing'
  | 'generating'
  | 'verifying'
  | 'complete'
  | 'failed'
  | 'cancelled';

export interface CreationInput {
  readonly audio: SelectedFile;
  readonly title: string;
  readonly artist: string;
  readonly cover?: SelectedFile | undefined;
  /**
   * How the player corrected the automatic result.
   *
   * Omitted is AUTO. The service does not interpret any of it — the
   * numbers travel to the engine, and what a number means is decided
   * there, in the one place chart generation lives.
   */
  readonly mode?: GeneratorMode | undefined;
  readonly tuning?: ChartTuning | undefined;
  /** Shapes, traversals, source bias and sections. Omitted is undirected. */
  readonly direction?: ChartDirection | undefined;
  /** Let the engine direct itself from the sections it detects. */
  readonly autoDirect?: boolean | undefined;
  /** Whether that direction may read structure. Omitted means yes. */
  readonly semantic?: boolean | undefined;
  /** Regenerating one section of a pack that already exists. */
  readonly baselineDirection?: ChartDirection | undefined;
  /** The engine's own direction per difficulty, as it reported it. */
  readonly resolvedDirections?: Readonly<Record<string, Record<string, unknown>>> | undefined;
  /** The pack's charts, so everything outside the section is copied. */
  readonly baselineCharts?: Readonly<Record<string, unknown>> | undefined;
  readonly regenerateSection?: number | undefined;
}

export interface CreatedSong {
  readonly summary: ChartGenerationSummary;
  /** The Song Pack v1 archive, byte for byte as the engine wrote it. */
  readonly archive: ArrayBuffer;
  /** The same archive, already parsed — proof it reads, and free to reuse. */
  readonly pack: LoadedSongPack;
  readonly audio: PreparedAudio;
  readonly eventCounts: Readonly<Record<Difficulty, number>>;
}

export interface CreationCallbacks {
  readonly onPhase?: (phase: CreationPhase) => void;
  /** The engine's own stages, forwarded unchanged. */
  readonly onProgress?: (progress: ChartGenerationProgress) => void;
  /** Free-text notes: what was converted, what was skipped, what was measured. */
  readonly onNote?: (note: string) => void;
}

export interface SongCreationServiceOptions {
  readonly client: ChartGenerationClient;
  /** Decodes compressed audio when the engine cannot. Usually `AudioEngine.decode`. */
  readonly decode: AudioDecoder;
  /** Injectable for tests. */
  readonly now?: () => Date;
}

export class SongCreationService {
  private readonly client: ChartGenerationClient;
  private readonly decode: AudioDecoder;
  private readonly now: () => Date;
  private currentPhase: CreationPhase = 'idle';
  private controller: AbortController | null = null;

  constructor(options: SongCreationServiceOptions) {
    this.client = options.client;
    this.decode = options.decode;
    this.now = options.now ?? (() => new Date());
  }

  get phase(): CreationPhase {
    return this.currentPhase;
  }

  /** True while a generation is in flight. The GENERATE button reads this. */
  get busy(): boolean {
    return (
      this.currentPhase === 'preparing' ||
      this.currentPhase === 'generating' ||
      this.currentPhase === 'verifying'
    );
  }

  /**
   * Asks the engine to stop.
   *
   * The HTTP request is aborted immediately and the engine is told to stand
   * down; it stops at its next stage boundary rather than mid-FFT. Either way
   * this call returns at once and nothing is imported.
   */
  cancel(): boolean {
    if (this.controller === null) return false;
    this.controller.abort();
    return true;
  }

  async create(input: CreationInput, callbacks: CreationCallbacks = {}): Promise<CreatedSong> {
    if (this.busy) {
      throw new ChartGenerationError('rejected', 'A song is already being generated.');
    }
    const title = input.title.trim();
    if (title.length === 0) throw new ChartGenerationError('rejected', 'A title is required.');
    const artist = input.artist.trim();

    const controller = new AbortController();
    this.controller = controller;
    try {
      this.setPhase('preparing', callbacks);

      // Asked every time rather than cached: the answer depends on what is
      // installed beside the engine, and the engine may have been restarted
      // with a different environment since the last generation.
      const health = await this.client.health();
      if (!health.ok) {
        throw new ChartGenerationError(
          'rejected',
          `The chart engine answered but is not ready (config: ${health.configPath}).`,
        );
      }
      callbacks.onNote?.(
        `Engine ${health.engine} ${health.bridgeVersion} on Python ${health.pythonVersion}; ` +
          `decodes ${health.acceptedAudio.join(', ')}.`,
      );

      const audio = await prepareAudio(input.audio, {
        accepted: health.acceptedAudio,
        decode: this.decode,
        maxBytes: health.maxUploadBytes,
      });
      callbacks.onNote?.(audio.note);

      const cover = input.cover === undefined ? undefined : await prepareCover(input.cover);
      if (cover !== undefined) callbacks.onNote?.(cover.note);

      const packId = derivePackId(this.now());
      this.setPhase('generating', callbacks);
      const generated = await this.client.generate(
        {
          packId,
          title,
          artist,
          ...(input.mode === undefined ? {} : { mode: input.mode }),
          ...(input.tuning === undefined ? {} : { tuning: input.tuning }),
          ...(input.direction === undefined ? {} : { direction: input.direction }),
          ...(input.autoDirect === true ? { autoDirect: true } : {}),
          ...(input.semantic === false ? { semantic: false } : {}),
          ...(input.regenerateSection === undefined
            ? {}
            : {
                ...(input.baselineDirection === undefined
                  ? {}
                  : { baselineDirection: input.baselineDirection }),
                ...(input.resolvedDirections === undefined
                  ? {}
                  : { resolvedDirections: input.resolvedDirections }),
                ...(input.baselineCharts === undefined
                  ? {}
                  : { baselineCharts: input.baselineCharts }),
                regenerateSection: input.regenerateSection,
              }),
          audio: { bytes: audio.bytes, fileName: audio.fileName, mimeType: audio.mimeType },
          ...(cover === undefined
            ? {}
            : {
                cover: {
                  bytes: cover.bytes,
                  fileName: cover.fileName,
                  mimeType: cover.mimeType,
                },
              }),
        },
        { onProgress: callbacks.onProgress ?? (() => undefined), signal: controller.signal },
      );

      this.setPhase('verifying', callbacks);
      const pack = readArchive(generated.archive);
      callbacks.onNote?.(
        `Song Pack v1 verified by the runtime's own reader: ` +
          `${pack.manifest.packId}, BPM ${pack.manifest.bpm}, ` +
          `${pack.charts.easy.events.length}/${pack.charts.normal.events.length}/` +
          `${pack.charts.hard.events.length} events.`,
      );

      this.setPhase('complete', callbacks);
      return {
        summary: generated.summary,
        archive: generated.archive,
        pack,
        audio,
        eventCounts: {
          easy: pack.charts.easy.events.length,
          normal: pack.charts.normal.events.length,
          hard: pack.charts.hard.events.length,
        },
      };
    } catch (cause) {
      const cancelled =
        controller.signal.aborted ||
        (cause instanceof ChartGenerationError && cause.code === 'cancelled');
      this.setPhase(cancelled ? 'cancelled' : 'failed', callbacks);
      throw cancelled
        ? new ChartGenerationError('cancelled', 'Generation was cancelled.', { cause })
        : cause;
    } finally {
      this.controller = null;
    }
  }

  private setPhase(phase: CreationPhase, callbacks: CreationCallbacks): void {
    this.currentPhase = phase;
    callbacks.onPhase?.(phase);
  }
}

/**
 * Parses the archive with the shipping reader, translating its rejections.
 *
 * A `SongPackError` here means the engine wrote something this runtime will not
 * accept, which is a compatibility failure and deserves to be labelled as one
 * rather than surfacing as a raw code.
 */
function readArchive(archive: ArrayBuffer): LoadedSongPack {
  try {
    return SongPackLoader.loadSync(archive);
  } catch (cause) {
    if (isSongPackError(cause)) {
      throw new ChartGenerationError(
        'engine-failed',
        `The engine produced an archive this runtime cannot read (${cause.code}: ${cause.message}).`,
        { cause },
      );
    }
    throw cause;
  }
}
